// T-027 Phase 5 — wire activity_log to Realtime with tiered invalidation.
//
// Mirrors useQcRealtime.js (Phase 3, commit 4430507) structure: single
// channel, RQ-invalidation only, no legacy bridge (every consumer is
// React Query — useDevDetail / useFavorites / UserManagement read via
// useState and are out of scope per Discovery).
//
// INSERT-only subscription: activity_log is append-only by
// Convention #56. Discovery confirmed zero UPDATE/DELETE writers
// across src/ and sql/.
//
// Tier A broad (every burst, immediate):
//   ['settings', 'activity', 'feed']  — paginated Activity Feed panel
//                                       (useActivityFeed.js:42)
//   ['settings-hub-ledger']           — Hub Recent Activity last 5 rows
//                                       (useSettingsActivityFeed.js:21,
//                                       prefix-matches with limit suffix)
//   ['settings-hub-stats']            — Hub envelope, Recent Activity
//                                       counts (useSettingsHubStats.js:15)
//
// Tier A narrow: none. Per-dev activity panels (useDevDetail.js:57-60)
// use useState, not React Query. No ['dev-activity', devId] consumer
// exists in src/.
//
// Tier B (trailing 10s debounce — fires once per quiet period):
//   ['settings', 'activity', 'handling']        — get_dev_handling RPC
//   ['settings', 'activity', 'productivity']    — get_team_productivity
//   ['settings', 'activity', 'stats']           — get_activity_stats
//   ['analytics', 'activity_feed']              — analytics_activity_feed
//   ['analytics', 'designer_pace_monthly']      — analytics_designer_pace_monthly
//
// Phase 2 / Phase 5 analytics overlap: useDevelopmentsRealtime
// invalidates the broad ['analytics'] prefix in its Tier B;
// useActivityLogRealtime invalidates the two activity-derived
// analytics keys strictly. On a dev edit (which mutates developments
// AND inserts an activity_log row), both hooks debounce
// independently. The two activity_log-derived analytics RPCs may
// refetch twice within ~10s of a burst settling — once per hook
// timer. Acceptable trade-off: cross-hook debounce coordination
// would violate hook isolation. The strict scope is still required
// for non-dev activity_log inserts (BRAND_MERGE in useBrandLogic.js,
// system RPCs in settings-03-system-rpcs.sql) where Phase 2 does not
// fire.

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useRealtimeSubscription } from './useRealtimeSubscription';

const TIER_A_BROAD_KEYS = [
  ['settings', 'activity', 'feed'],
  ['settings-hub-ledger'],
  ['settings-hub-stats'],
];

const TIER_B_PREFIX_KEYS = [
  ['settings', 'activity', 'handling'],
  ['settings', 'activity', 'productivity'],
  ['settings', 'activity', 'stats'],
  ['analytics', 'activity_feed'],
  ['analytics', 'designer_pace_monthly'],
];

const TIER_B_DEBOUNCE_MS = 10_000;

export function useActivityLogRealtime({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const tierBTimerRef = useRef(null);

  // Tier B trailing-edge debounce. Resets on every event in a burst;
  // fires once 10s after the last event lands.
  const scheduleTierB = () => {
    const wasScheduled = tierBTimerRef.current !== null;
    if (tierBTimerRef.current) clearTimeout(tierBTimerRef.current);

    if (!wasScheduled) {
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'activity_log.tier_b_scheduled',
      });
    }

    tierBTimerRef.current = setTimeout(() => {
      tierBTimerRef.current = null;
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'activity_log.tier_b_fired',
      });
      try {
        TIER_B_PREFIX_KEYS.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useActivityLogRealtime.tierB' },
        });
      }
    }, TIER_B_DEBOUNCE_MS);
  };

  // Cleanup pending Tier B on unmount so a fired timer doesn't try to
  // invalidate against a torn-down QueryClient.
  useEffect(() => {
    return () => {
      if (tierBTimerRef.current) {
        clearTimeout(tierBTimerRef.current);
        tierBTimerRef.current = null;
      }
    };
  }, []);

  useRealtimeSubscription({
    table: 'activity_log',
    event: 'INSERT',
    enabled,
    onPayload: (events) => {
      const affectedActions = events
        .map(e => e.new?.action)
        .filter(Boolean);

      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: 'activity_log.tier_a_invalidated',
        data: {
          batch_size: events.length,
          actions: affectedActions.slice(0, 5),
        },
      });

      try {
        TIER_A_BROAD_KEYS.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { source: 'useActivityLogRealtime.tierA' },
        });
      }

      // Tier B: schedule (or reset) the trailing-edge debounce.
      scheduleTierB();
    },
  });
}
